import { FC, useEffect, useRef, useState } from 'react'
import { useLayout } from '../../../_metronic/layout/core'
import { BankAccount, PaymentMethod } from './models'
import { CardPayment } from './components/CardPaymentMethod'
import { PayoutMethodModel } from './components/PayoutMethodModel'
import { CardGetPaid } from './components/CardGetPaid'
import { PaymentMethodModel } from './components/PaymentMethodModel'
import { deletePaymentMethod, deletePayoutMethod, getMyPaymentMethods, getMyPayoutMethods } from '../../services/financialService'
import { useConfirmAction } from '../../hooks/utils/useConfirmAction'
import { showErrorAlert } from '../../helpers/alert'

const PaymentsMethodPage: FC = () => {
  const { config, setLayout } = useLayout()
  const restoreRef = useRef(config)
  const [showPayout, setShowPayout] = useState(false)
  const [showPayment, setShowPayment] = useState(false)
  const [listPaymentMethods, setListPaymentMethods] = useState<PaymentMethod[]>([])
  const [listPayoutMethodsCommercial, setListPayoutMethodsCommercial] = useState<BankAccount[]>([])
  const [listPayoutMethodsImpact, setListPayoutMethodsImpact] = useState<BankAccount[]>([])
  const [payoutSelected, setPayoutSelected] = useState<BankAccount | null>(null)
  const confirm = useConfirmAction()
  const limit = 100

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: { ...config.header, display: true },
      aside: { ...config.aside, display: true },
      toolbar: { ...config.toolbar, display: false },
      footer: { ...config.footer, display: true },
    })
    getListPaymentMethods()
    getListPayoutMethods()

    return () => setLayout(restoreRef.current)
  }, [])

  const getListPaymentMethods = async () => {
    let payments = await getMyPaymentMethods({offset: 0, limit: limit});
    setListPaymentMethods(payments)
  }
  
  const getListPayoutMethods = async () => {
    let payouts = await getMyPayoutMethods({offset: 0, limit: limit});
    let commercial_ = (payouts.results || []).filter((item:BankAccount) => item.typeId?.toLowerCase() === 'commercial')
    let impact_ = (payouts.results || []).filter((item:BankAccount) => item.typeId?.toLowerCase() !== 'commercial')
    setListPayoutMethodsCommercial(commercial_)
    setListPayoutMethodsImpact(impact_)
  }

  const handleOnCreatePayoutMethod = () => {
    setShowPayout(false)
    getListPayoutMethods()
  }

  const handleOnDeletePayout = async (id:number) => {
    try {
      let isConfirm = await confirm({title: 'Are you sure you want to delete this payout method?', message: 'This action cannot be undone.'})
      if(isConfirm){
        let isDeleted = await deletePayoutMethod(id, {})
        if(isDeleted.success){
          showErrorAlert({title: 'Deleted', message: isDeleted.message })
          getListPayoutMethods()
        }
      }
    } catch (error) {
      console.log('error',error)
    }
  }
  
  const handleOnEditPayout = async (data:BankAccount) => {
    try {
      setPayoutSelected(data)
      setShowPayout(true)
    } catch (error) {
      console.log('error',error)
    }
  }

  const handleOnClosePayout = () => {
    setPayoutSelected(null)
    setShowPayout(false)
  }

  const handleOnCreatePaymentMethod = (v) => {
    setShowPayment(false)
    getListPaymentMethods()
  }

  const onHandleDeletePayment = async (id:string) => {
    try {
      let isConfirm = await confirm({title: 'Are you sure you want to delete this payment method?', message: 'This action cannot be undone.'})
      if(isConfirm){
        let isDeleted = await deletePaymentMethod(id, {})
        if(isDeleted.success){
          showErrorAlert({title: 'Deleted', message: isDeleted.message })
          getListPaymentMethods()
        }
      }
    } catch (error) {
      console.log('error',error)
    }
  }

  return (<div className="payments-method">
    <div className="payments-method__section">
      <h1 className="payments-method__title">Payments</h1>
      <span className="payments-method__subtitle">Payment method</span>

      <div className="payments-method__cards">
        {(listPaymentMethods?.length == 0) ? <p className="payments-method-column__desc">Add a payment method to proceed with the payment.</p> : null}
        {(listPaymentMethods ?? []).map((item,index)=><CardPayment key={`item-payment-method-${index}`} card={item} onEdit={()=>{}} onDelete={onHandleDeletePayment}/>)}
        
      </div>
      <div className='w-100 flex-column'>
        <div className='add-button-no-border' onClick={()=>setShowPayment(true)}>
          + Add another card
        </div>
      </div>

    </div>

    <div className="payments-method__section">
      <div className="payments-method__info">
        <span className="payments-method__subtitle">
          How you’ll get paid
        </span>
        {/* <p className="payments-method__description">lorem</p> */}
      </div>

      <div className="payments-method__columns">
        {(listPayoutMethodsCommercial?.length == 0 && listPayoutMethodsImpact?.length == 0) ? <p className="payments-method-column__desc">Add at least payout method so we know where to send your money</p> : null}
        {listPayoutMethodsCommercial && listPayoutMethodsCommercial?.length>0 && (<Column list={listPayoutMethodsCommercial} title="Commercial" handleOnEdit={handleOnEditPayout} handleOnDelete={handleOnDeletePayout} />)}
        {listPayoutMethodsImpact && listPayoutMethodsImpact?.length>0 && (<Column list={listPayoutMethodsImpact} title="Impact / Philanthropy" handleOnEdit={handleOnEditPayout} handleOnDelete={handleOnDeletePayout} />)}
      </div>
      <div className='w-100 flex-column'>
        <div className='add-button-no-border' onClick={()=>setShowPayout(true)}>
          + Add more method
        </div>
      </div>
    </div>
    <PayoutMethodModel show={showPayout} data={payoutSelected} onClose={handleOnClosePayout} onSubmit={handleOnCreatePayoutMethod} />
    <PaymentMethodModel show={showPayment} onClose={() => setShowPayment(false)} onSubmit={handleOnCreatePaymentMethod} />
  </div>)
}

interface PropColumn {
  title:string
  list:BankAccount[]
  handleOnEdit: (data: BankAccount) => void
  handleOnDelete: (id: string) => void
}
const Column:FC<PropColumn> = ({ title, list, handleOnEdit, handleOnDelete }) => {
  return (
    <div className="payments-method-column">
      <span className="payments-method-column__title">{title}</span>
      {/* <p className="payments-method-column__desc">lorem</p> */}
      {(list ?? []).map((item,index)=><CardGetPaid key={`item-get-paid-${index}`} card={item} onEdit={handleOnEdit} onDelete={handleOnDelete} />)}
    </div>
  );
}


export { PaymentsMethodPage }